import React from 'react';
import { useTranslation } from 'react-i18next';
import CustomButton from '../../../components/CustomButton';
import NativeSelect from '../../../components/ui/NativeSelect';
import { tableCellClass, tableHeaderCellClass } from '../../../components/TableCard';
import { useGetAdminGroupsQuery } from '../../../api/group/groupAdminApiSlice';
import { useGetLocationListQuery } from '../../../api/locations/locationsApiSlice';
import { useSetOrganizationRoomAccessMutation } from '../../../api/organization/organizationApiSlice';
import {
    OrganizationAccessMode,
    OrganizationRoomGrant,
    SpaceAgreement,
} from '../../../api/organization/organizationApiTypes';
import { isRoomCell } from '../../../utils/roomUseCases';
import { isAgreementBackingBenefits } from '../spaceAgreementStatus';
import {
    AssignmentDraft,
    coverAll,
    draftFromGrants,
    draftToGrantInputs,
    isDraftDirty,
    publicWithoutContract,
    roomsChangingOpenness,
    setAccess,
    toggleRoom,
    updateRoom,
} from '../roomAssignmentDraft';
import RoomOpennessWarning from './RoomOpennessWarning';

interface Props {
    organizationId: string;
    grants: OrganizationRoomGrant[];
    agreements: SpaceAgreement[];
    onClose: () => void;
}

/**
 * "Räume zuordnen" (option C, docs/plans/coverage-per-room-assignment.md §4.1).
 *
 * The tenant's rooms (venue and apartment cells), filterable by location. Per
 * room: assigned or not; "Zugriff" — public (everyone, externals included),
 * shared with the assigned organizations, or dedicated to this one (ADR-0008
 * amendment 2026-10-13); and covered by one of this organization's contracts
 * or "members pay". Every assignment needs a running room contract — public,
 * shared or dedicated alike (PO, 2026-10-20; the API refuses an addition without
 * one, spaceAccess.ts › additionsWithoutRunningContract). A bulk action covers every assigned
 * room with one contract. One unit, one Save: it is opened with "Räume
 * zuordnen", saved or cancelled, and closes after a successful save.
 */
const OrgRoomAssignmentEditor: React.FC<Props> = ({
    organizationId,
    grants,
    agreements,
    onClose,
}) => {
    const { t } = useTranslation();
    const saved = React.useMemo(() => draftFromGrants(grants), [grants]);
    const [draft, setDraft] = React.useState<AssignmentDraft>(saved);
    const [locationFilter, setLocationFilter] = React.useState('');
    const [bulkAgreementId, setBulkAgreementId] = React.useState('');
    const [error, setError] = React.useState<string | null>(null);

    const { data: groups, isLoading: groupsLoading, isError: groupsError } = useGetAdminGroupsQuery();
    const { data: locations } = useGetLocationListQuery(undefined);
    const [saveAccess, { isLoading: isSaving }] = useSetOrganizationRoomAccessMutation();

    const rooms = React.useMemo(
        () => (groups ?? [])
            .filter((group) => isRoomCell(group.compartmentTypes))
            .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '')),
        [groups],
    );
    const locationName = (locationId: string | null) =>
        locations?.find((location) => location.id === locationId)?.name ?? t('common.not_applicable');
    const roomLocations = React.useMemo(
        () => Array.from(new Set(rooms.map((room) => room.locationId).filter((id): id is string => !!id))),
        [rooms],
    );
    const visibleRooms = rooms.filter(
        (room) => !locationFilter || room.locationId === locationFilter,
    );

    // Exactly the contracts the server treats as covering: not archived and
    // their ending not confirmed. A lapsed date without notice still covers
    // (#2165) and must stay offered — it can be the one contract a customer's
    // rooms run on. A room still linked to a contract that no longer covers
    // keeps showing it, labelled as ended, so the operator sees it and can
    // change it.
    const coveringAgreements = agreements.filter(isAgreementBackingBenefits);
    const coveringAgreementIds = coveringAgreements.map((agreement) => agreement.id);
    const linkedLabel = (id: string) => {
        const agreement = agreements.find((candidate) => candidate.id === id);
        if (!agreement) return t('organization.rooms.agreement_archived');
        return t('organization.rooms.coverage.ended', { name: agreement.name });
    };

    const dirty = isDraftDirty(draft, saved);
    const missingContract = publicWithoutContract(draft);
    const roomName = (groupId: string): string =>
        rooms.find((room) => room.id === groupId)?.name?.trim() || String(t('common.not_applicable'));

    const save = async () => {
        setError(null);
        try {
            await saveAccess({ organizationId, grants: draftToGrantInputs(draft) }).unwrap();
            onClose();
        } catch (err) {
            // The API owns the invariants (dedicated rooms, the owner gate) and
            // names what stands in the way, so its message is shown as it is.
            const e = err as { data?: { message?: string, violations?: Array<{ message?: string }> } };
            const violations = (e?.data?.violations ?? []).map((v) => v.message).filter(Boolean);
            setError(violations.length > 0
                ? violations.join(' ')
                : e?.data?.message ?? t('organization.rooms.assign.save_error'));
        }
    };

    const coverageSelect = (groupId: string) => {
        const entry = draft[groupId];
        if (!entry) return null;
        const value = entry.spaceAgreementId ?? '';
        const linkedButNotOffered = value && !coveringAgreements.some((a) => a.id === value);
        return (
            <NativeSelect
                aria-label={t('organization.rooms.columns.coverage')}
                value={value}
                onChange={(event) =>
                    setDraft(updateRoom(draft, groupId, { spaceAgreementId: event.target.value || null }))}
            >
                {/* A public room is open to everyone anyway: "access only" would change nothing. */}
                {entry.accessMode !== 'public' && (
                    <option value={''}>{t('organization.rooms.coverage.access_only')}</option>
                )}
                {entry.accessMode === 'public' && !value && (
                    <option value={''}>{t('organization.rooms.assign.pick_contract')}</option>
                )}
                {linkedButNotOffered && <option value={value}>{linkedLabel(value)}</option>}
                {coveringAgreements.map((agreement) => (
                    <option key={agreement.id} value={agreement.id}>
                        {t('organization.rooms.coverage.by', { name: agreement.name })}
                    </option>
                ))}
            </NativeSelect>
        );
    };

    const renderRooms = () => {
        if (groupsLoading) return <p className={'text-sm text-foreground/60'}>{t('common.loading')}</p>;
        // A failure must never look like "there are no rooms" (#1579).
        if (groupsError) return <p className={'text-sm text-foreground/60'}>{t('organization.rooms.assign.rooms_load_error')}</p>;
        if (rooms.length === 0) return <p className={'text-sm text-foreground/60'}>{t('organization.rooms.assign.no_rooms')}</p>;
        return (
            <div className={'overflow-x-auto'}>
                <table className={'w-full text-sm'}>
                    <thead>
                        <tr className={'border-b border-foreground/5'}>
                            <th className={tableHeaderCellClass}>{t('organization.rooms.assign.assigned')}</th>
                            <th className={tableHeaderCellClass}>{t('organization.rooms.columns.room')}</th>
                            <th className={tableHeaderCellClass}>{t('organization.rooms.columns.access')}</th>
                            <th className={tableHeaderCellClass}>{t('organization.rooms.columns.coverage')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {visibleRooms.map((room) => {
                            const entry = draft[room.id];
                            return (
                                <tr key={room.id} className={'border-b border-foreground/5 last:border-b-0'}>
                                    <td className={tableCellClass}>
                                        <input
                                            type={'checkbox'}
                                            aria-label={room.name ?? room.id}
                                            checked={!!entry}
                                            // No room is assigned without a running contract
                                            // (PO, 2026-10-20); one already assigned can still
                                            // be removed.
                                            disabled={!entry && coveringAgreements.length === 0}
                                            onChange={(event) =>
                                                setDraft(toggleRoom(draft, room.id, event.target.checked, saved))}
                                            className={'h-4 w-4 accent-primary disabled:opacity-40 disabled:cursor-not-allowed'}
                                        />
                                    </td>
                                    <td className={tableCellClass}>
                                        <div>{room.name?.trim() || t('common.not_applicable')}</div>
                                        <div className={'text-xs text-foreground/60'}>{locationName(room.locationId)}</div>
                                    </td>
                                    <td className={tableCellClass}>
                                        {entry && (
                                            <NativeSelect
                                                aria-label={t('organization.rooms.columns.access')}
                                                value={entry.accessMode}
                                                onChange={(event) => setDraft(setAccess(
                                                    draft,
                                                    room.id,
                                                    event.target.value as OrganizationAccessMode,
                                                    coveringAgreementIds,
                                                ))}
                                            >
                                                {(coveringAgreements.length > 0 || entry.accessMode === 'public') && (
                                                    <option value={'public'}>{t('organization.rooms.mode.public')}</option>
                                                )}
                                                <option value={'shared'}>{t('organization.rooms.mode.shared')}</option>
                                                <option value={'dedicated'}>{t('organization.rooms.mode.dedicated')}</option>
                                            </NativeSelect>
                                        )}
                                    </td>
                                    <td className={tableCellClass}>{coverageSelect(room.id)}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <div className={'flex flex-col gap-4 rounded-[5px] border border-foreground/10 px-4 py-4'}>
            <p className={'text-sm text-foreground/60'}>{t('organization.rooms.assign.intro')}</p>

            {roomLocations.length > 1 && (
                <div className={'w-full sm:w-64'}>
                    <label className={'block text-sm text-foreground/60 mb-1'} htmlFor={'room-location-filter'}>
                        {t('organization.rooms.assign.location_filter')}
                    </label>
                    <NativeSelect
                        id={'room-location-filter'}
                        value={locationFilter}
                        onChange={(event) => setLocationFilter(event.target.value)}
                    >
                        <option value={''}>{t('organization.rooms.assign.all_locations')}</option>
                        {roomLocations.map((id) => (
                            <option key={id} value={id}>{locationName(id)}</option>
                        ))}
                    </NativeSelect>
                </div>
            )}

            {coveringAgreements.length > 0 && Object.keys(draft).length > 0 && (
                <div className={'flex flex-col sm:flex-row sm:items-end gap-2'}>
                    <div className={'w-full sm:w-72'}>
                        <label className={'block text-sm text-foreground/60 mb-1'} htmlFor={'room-cover-all'}>
                            {t('organization.rooms.assign.cover_all_label')}
                        </label>
                        <NativeSelect
                            id={'room-cover-all'}
                            value={bulkAgreementId}
                            onChange={(event) => setBulkAgreementId(event.target.value)}
                        >
                            <option value={''}>{t('organization.rooms.assign.cover_all_pick')}</option>
                            {coveringAgreements.map((agreement) => (
                                <option key={agreement.id} value={agreement.id}>{agreement.name}</option>
                            ))}
                        </NativeSelect>
                    </div>
                    <CustomButton
                        title={t('organization.rooms.assign.cover_all_apply')}
                        isFilled={false}
                        disabled={!bulkAgreementId}
                        onClick={() => setDraft(coverAll(draft, bulkAgreementId))}
                    />
                </div>
            )}

            {coveringAgreements.length === 0 && (
                <p className={'text-sm text-foreground/60'}>{t('organization.rooms.assign.no_agreements')}</p>
            )}

            {renderRooms()}

            {roomsChangingOpenness(draft, saved).map((groupId) => (
                <RoomOpennessWarning
                    key={`${groupId}-${draft[groupId].accessMode}`}
                    groupId={groupId}
                    roomName={roomName(groupId)}
                    organizationId={organizationId}
                    willBeOpen={draft[groupId].accessMode === 'public'}
                />
            ))}

            {missingContract.length > 0 && (
                <p className={'text-sm text-foreground'}>
                    {t('organization.rooms.assign.public_needs_contract', {
                        rooms: missingContract.map(roomName).join(', '),
                    })}
                </p>
            )}

            {error && <p className={'text-sm text-foreground'}>{error}</p>}

            <div className={'flex gap-3'}>
                <CustomButton title={t('common.cancel')} isFilled={false} onClick={onClose} />
                <CustomButton
                    title={t('common.save')}
                    onClick={() => { void save(); }}
                    loading={isSaving}
                    disabled={!dirty || isSaving || missingContract.length > 0}
                />
            </div>
        </div>
    );
};

export default OrgRoomAssignmentEditor;
